/**
 * The arithmetic behind the cue editor, kept apart from React so it can be tested.
 *
 * Three concerns live here:
 *
 * **Where a marker is on screen.** The waveform scrolls and zooms, so a marker's
 * pixel is a function of the visible window, not of the file. Every conversion
 * goes through one `View` so the overlay and the hit-test can never disagree.
 *
 * **Which marker a click means.** A 2px line is not a target anyone can hit at
 * speed. Each marker owns an explicit buffer either side of its line, and when
 * two buffers overlap the nearer line wins -- not whichever was drawn last, which
 * is what stacked DOM hit areas would give you.
 *
 * **Which orderings are allowed.** The engine refuses a Cue In after the Early Out
 * (nova/library/cuepoints.py). Refusing on save is correct but late: a curator
 * who drags a marker through another one should see it stop, not learn about it
 * from an error after pressing Ctrl+S. So a drag is clamped to the same rules the
 * engine enforces.
 */

import type { CueField } from "../types";

/** Pixels either side of a marker line that still grab it. */
export const GRAB_PX = 8;

/** A dragged marker snaps to the playhead when released this close to it. */
export const SNAP_PX = 6;

/** Smallest gap kept between markers that must be strictly ordered. */
export const MIN_GAP = 0.01;

export interface MarkerDef {
  field: CueField;
  label: string;
  /** Short flag text drawn on the waveform. */
  flag: string;
  /** CSS custom property holding the marker colour. */
  color: string;
  /** Key that drops this marker at the playhead. */
  key: string;
}

/** In record order. Colours follow the broadcast convention the spec names. */
export const MARKERS: MarkerDef[] = [
  { field: "cueIn", label: "Start", flag: "START", color: "var(--cue-in)", key: "I" },
  { field: "intro", label: "Intro", flag: "INTRO", color: "var(--cue-intro)", key: "T" },
  { field: "hookStart", label: "Highlight start", flag: "[", color: "var(--cue-hook)", key: "[" },
  { field: "hookEnd", label: "Highlight end", flag: "]", color: "var(--cue-hook)", key: "]" },
  { field: "cueOut", label: "Mix out", flag: "OUT", color: "var(--cue-out)", key: "O" },
];

export const MARKER_BY_FIELD = Object.fromEntries(MARKERS.map((m) => [m.field, m])) as Record<
  CueField,
  MarkerDef
>;

export type CueValues = Record<CueField, number | null>;

/** The visible window of the waveform. */
export interface View {
  /** Seconds at the left edge. */
  start: number;
  /** Horizontal scale. */
  pxPerSec: number;
  /** Visible width in pixels. */
  width: number;
}

export function timeToX(t: number, view: View): number {
  return (t - view.start) * view.pxPerSec;
}

export function xToTime(x: number, view: View): number {
  return view.start + x / Math.max(view.pxPerSec, 1e-9);
}

/**
 * The marker a pointer at `x` means, or null.
 *
 * Nearest line within `grab` pixels. Distance ties go to the later marker in
 * record order, because two markers only coincide when one was just dragged
 * onto the other and the later one (an Early Out onto a hook end, say) is the one
 * that stops a drag -- pulling it back out is the likely intent.
 */
export function nearestMarker(
  x: number,
  values: Partial<CueValues>,
  view: View,
  grab = GRAB_PX
): CueField | null {
  let best: CueField | null = null;
  let bestDistance = Infinity;
  for (const { field } of MARKERS) {
    const t = values[field];
    if (t == null) continue;
    const distance = Math.abs(timeToX(t, view) - x);
    if (distance <= grab && distance <= bestDistance) {
      best = field;
      bestDistance = distance;
    }
  }
  return best;
}

/**
 * Where `field` may actually go if asked to go to `t`.
 *
 * The same rules as the engine's `check_order`: inside the file; Early Out after
 * Cue In; Intro between them; a hook that ends after it starts. Clamping instead
 * of refusing is what makes a drag feel like it hit a wall rather than broke.
 */
export function clampField(
  field: CueField,
  t: number,
  values: Partial<CueValues>,
  duration: number
): number {
  let lo = 0;
  let hi = Math.max(0, duration);
  const cueIn = values.cueIn ?? null;
  const intro = values.intro ?? null;
  const cueOut = values.cueOut ?? null;

  switch (field) {
    case "cueIn":
      if (intro != null) hi = Math.min(hi, intro);
      if (cueOut != null) hi = Math.min(hi, cueOut - MIN_GAP);
      break;
    case "intro":
      if (cueIn != null) lo = Math.max(lo, cueIn);
      if (cueOut != null) hi = Math.min(hi, cueOut);
      break;
    case "cueOut":
      if (cueIn != null) lo = Math.max(lo, cueIn + MIN_GAP);
      if (intro != null) lo = Math.max(lo, intro);
      break;
    case "hookStart":
      if (values.hookEnd != null) hi = Math.min(hi, values.hookEnd - MIN_GAP);
      break;
    case "hookEnd":
      if (values.hookStart != null) lo = Math.max(lo, values.hookStart + MIN_GAP);
      break;
  }
  if (hi < lo) return lo;
  return Math.min(hi, Math.max(lo, t));
}

/** Rounded to the millisecond. */
export function roundMs(t: number): number {
  return Math.round(t * 1000) / 1000;
}

/** `m:ss.mmm`, or `h:mm:ss.mmm` past an hour. Negative values keep their sign. */
export function formatTime(seconds: number | null | undefined, withMs = true): string {
  if (seconds == null || !Number.isFinite(seconds)) return "—";
  const sign = seconds < 0 ? "-" : "";
  const total = Math.abs(seconds);
  let whole = Math.floor(total);
  let ms = Math.round((total - whole) * 1000);
  if (ms === 1000) {
    whole += 1;
    ms = 0;
  }
  const h = Math.floor(whole / 3600);
  const m = Math.floor((whole % 3600) / 60);
  const s = whole % 60;
  const ss = String(s).padStart(2, "0");
  const tail = withMs ? `.${String(ms).padStart(3, "0")}` : "";
  return h > 0
    ? `${sign}${h}:${String(m).padStart(2, "0")}:${ss}${tail}`
    : `${sign}${m}:${ss}${tail}`;
}

/**
 * Parse what a curator types into a cue box.
 *
 * Accepts `1:23.456`, `83.456`, `83`, `1:02:03.5`, and a comma for the decimal
 * point, since a Nigerian keyboard layout may well produce one. Returns null for
 * anything else rather than guessing.
 */
export function parseTime(raw: string): number | null {
  const text = raw.trim().replace(",", ".");
  if (!text) return null;
  if (!/^\d+(:\d{1,2}){0,2}(\.\d+)?$/.test(text)) return null;
  const parts = text.split(":");
  let total = 0;
  for (const part of parts) total = total * 60 + Number(part);
  return Number.isFinite(total) ? total : null;
}

/**
 * Fields a draft changed, split into what to set and what to return to detected.
 *
 * A field that was set by a person and is now empty is a clear. A field that was
 * detected and is unchanged is not sent at all -- sending it would turn the
 * analyser's reading into a curator's answer that nobody gave.
 */
export function diffCues(
  original: Partial<CueValues>,
  draft: Partial<CueValues>
): { changes: Partial<Record<CueField, number>>; clear: CueField[] } {
  const changes: Partial<Record<CueField, number>> = {};
  const clear: CueField[] = [];
  for (const { field } of MARKERS) {
    const before = original[field] ?? null;
    const after = draft[field] ?? null;
    if (before === after) continue;
    if (after === null) clear.push(field);
    // Compared at the resolution that is stored and pushed: a value that rounds to
    // the same millisecond is the same cue point, whatever its float noise.
    else if (before === null || roundMs(before) !== roundMs(after)) changes[field] = roundMs(after);
  }
  return { changes, clear };
}
